import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'

import type { REGION } from '@/utils/constants'

import { PLACEMENT } from './appSlice'

interface DigitalTryInState {
  selectedColorCheckerKey: Record<PLACEMENT, REGION> | null
  selectedColorCheckerShade: SelectedColorCheckerShade
  colorCheckerElements: ColorCheckerElements
  selectedColorCheckerHeight: Record<PLACEMENT, Record<string, number>>
  isVerticallyAligned: boolean
}

interface SelectedColorCheckerShade {
  [placement: string]: {
    [region: string]: {
      L: number | string | null
      a: number | string | null
      b: number | string | null
    }
  }
}

interface ColorCheckerElements {
  [placement: string]: {
    [region: string]: {
      id: string
      x: number
      y: number
      color: string
      labColor: {
        L: number | string | null
        a: number | string | null
        b: number | string | null
      }
      isLight: boolean
      placement: PLACEMENT
      region: REGION
      width: number
      height: number
    }
  }
}

const initialState: DigitalTryInState = {
  selectedColorCheckerKey: null,
  selectedColorCheckerShade: {},
  colorCheckerElements: {},
  selectedColorCheckerHeight: {
    [PLACEMENT.LEFT]: {
      Cervical: 40,
      Middle: 40,
      Incisal: 40,
    },
    [PLACEMENT.RIGHT]: {
      Cervical: 40,
      Middle: 40,
      Incisal: 40,
    },
  },
  isVerticallyAligned: false,
}

const digitalTryInSlice = createSlice({
  name: 'digitalTryIn',
  initialState,
  reducers: {
    setSelectedColorCheckerKey: (
      state,
      action: PayloadAction<Record<PLACEMENT, REGION> | null>,
    ) => {
      return {
        ...state,
        selectedColorCheckerKey: action.payload,
      }
    },
    setSelectedColorCheckerShade: (
      state,
      action: PayloadAction<SelectedColorCheckerShade>,
    ) => {
      const mergedShades = { ...state.selectedColorCheckerShade }

      // Merge each placement
      Object.entries(action.payload).forEach(([placement, regions]) => {
        mergedShades[placement] = {
          ...mergedShades[placement],
          ...regions,
        }
      })

      return {
        ...state,
        selectedColorCheckerShade: mergedShades,
      }
    },
    setSelectedColorCheckerHeight: (
      state,
      action: PayloadAction<{
        placement: PLACEMENT
        region: REGION
        height: number
      }>,
    ) => {
      state.selectedColorCheckerHeight[action.payload.placement][
        action.payload.region
      ] = action.payload.height
    },
    resetSelectedColorCheckerHeight: (state) => {
      return {
        ...state,
        selectedColorCheckerHeight: state.selectedColorCheckerHeight,
        isVerticallyAligned: state.isVerticallyAligned,
      }
    },
    resetColorCheckerState: (state) => {
      return {
        ...state,
        selectedColorCheckerKey: null,
        selectedColorCheckerShade: {},
        colorCheckerElements: {},
        isVerticallyAligned: state.isVerticallyAligned,
      }
    },
    removeSelectedColorCheckerShade: (
      state,
      action: PayloadAction<{ placement: PLACEMENT; region: REGION }>,
    ) => {
      const { placement, region } = action.payload

      if (state.selectedColorCheckerShade[placement]?.[region]) {
        delete state.selectedColorCheckerShade[placement][region]

        // If the placement has no more regions, remove the placement object
        if (
          Object.keys(state.selectedColorCheckerShade[placement]).length === 0
        ) {
          delete state.selectedColorCheckerShade[placement]
        }
      }
    },
    removeSelectedColorCheckerShadeByPlacement: (
      state,
      action: PayloadAction<PLACEMENT>,
    ) => {
      const placement = action.payload
      if (state.selectedColorCheckerShade[placement]) {
        delete state.selectedColorCheckerShade[placement]
      }
    },
    addColorCheckerElement: (
      state,
      action: PayloadAction<{
        placement: PLACEMENT
        region: REGION
        id: string
        x: number
        y: number
        centerX: number
        centerY: number
        color: string
        labColor: {
          L: number | string | null
          a: number | string | null
          b: number | string | null
        }
        isLight: boolean
        width: number
        height: number
      }>,
    ) => {
      const { placement, region } = action.payload

      if (!state.colorCheckerElements[placement]) {
        state.colorCheckerElements[placement] = {}
      }

      state.colorCheckerElements[placement][region] = action.payload
    },
    removeColorCheckerElement: (
      state,
      action: PayloadAction<{ placement: PLACEMENT; region: REGION }>,
    ) => {
      const { placement, region } = action.payload

      if (state.colorCheckerElements[placement]?.[region]) {
        delete state.colorCheckerElements[placement][region]

        // If the placement has no more regions, remove the placement object
        if (Object.keys(state.colorCheckerElements[placement]).length === 0) {
          delete state.colorCheckerElements[placement]
        }
      }
    },
    removeColorCheckerElementsByPlacement: (
      state,
      action: PayloadAction<PLACEMENT>,
    ) => {
      const placement = action.payload
      if (state.colorCheckerElements[placement]) {
        delete state.colorCheckerElements[placement]
      }
    },
    updateColorCheckerPosition: (
      state,
      action: PayloadAction<{
        placement: PLACEMENT
        region: REGION
        data: any
      }>,
    ) => {
      const { placement, region, data } = action.payload
      if (state.colorCheckerElements[placement]?.[region]) {
        state.colorCheckerElements[placement][region] = data
      }
    },
    resetEnamelStudio: (state) => {
      return {
        ...state,
        enamelStudioImages: {},
        isVerticallyAligned: state.isVerticallyAligned,
      }
    },
    resetState: (state) => {
      return {
        ...initialState,
        isVerticallyAligned: state.isVerticallyAligned,
      }
    },
    setIsVerticallyAligned: (state, action: PayloadAction<boolean>) => {
      state.isVerticallyAligned = action.payload
    },
  },
})

export const {
  setSelectedColorCheckerKey,
  setSelectedColorCheckerShade,
  removeSelectedColorCheckerShade,
  removeSelectedColorCheckerShadeByPlacement,
  resetColorCheckerState,
  resetEnamelStudio,
  resetState,
  addColorCheckerElement,
  removeColorCheckerElement,
  removeColorCheckerElementsByPlacement,
  updateColorCheckerPosition,
  setSelectedColorCheckerHeight,
  resetSelectedColorCheckerHeight,
  setIsVerticallyAligned,
} = digitalTryInSlice.actions

export default digitalTryInSlice.reducer
